<script setup lang="ts">
import { ref, computed, onMounted } from "vue";
import { RouterLink, useRoute } from "vue-router";
import {
  AddIcon,
  SearchIcon,
  ArrowRightIcon,
  DatabaseIcon,
  UploadIcon,
  LinkIcon,
  TableIcon,
} from "../icons";
import { api, errorText, dateTime, useSession } from "../api";
import WorkspaceUsage from "../components/WorkspaceUsage.vue";
import DatasetAvatar from "../components/DatasetAvatar.vue";
const route = useRoute(),
  session = useSession(),
  status = ref("active"),
  deletions = ref<any[]>([]),
  showDeletions = ref(false);
const isAdmin = computed(() =>
  ["owner", "admin"].includes(
    session.organizations.find((o) => o.id === session.organization)?.role,
  ),
);
async function loadDeletions() {
  try {
    deletions.value = await api("/api/dataset-deletions");
  } catch (e) {
    error.value = errorText(e);
  }
}
async function retryDeletion(id: string) {
  try {
    await api("/api/dataset-deletions/" + id + "/retry", { method: "POST" });
    await loadDeletions();
  } catch (e) {
    error.value = errorText(e);
  }
}
const datasets = ref<any[]>([]),
  search = ref(""),
  loading = ref(true),
  error = ref("");
const filtered = computed(() =>
  datasets.value.filter((d) =>
    d.name.toLocaleLowerCase().includes(search.value.toLocaleLowerCase()),
  ),
);
async function load() {
  loading.value = true;
  try {
    datasets.value = await api("/api/datasets?status=" + status.value);
    error.value = "";
  } catch (e) {
    error.value = errorText(e);
  } finally {
    loading.value = false;
  }
}
onMounted(load);
const deletionStatus: Record<string, string> = {
  pending: "Afventer sletning",
  running: "Sletning i gang",
  failed: "Sletning fejlede",
  succeeded: "Slettet",
};
</script>
<template>
  <section class="page-heading">
    <div>
      <h1>Dine data, samlet.</h1>
      <p class="subtitle">Fra den første række til det næste gode spørgsmål.</p>
    </div>
    <RouterLink class="button primary" to="/new"
      ><AddIcon :size="18" />Nyt datasæt</RouterLink
    >
  </section>
  <p v-if="route.query.deleted" class="notice" role="status">
    Datasættet er markeret til permanent sletning. Data ryddes op ved næste
    natlige kørsel.
  </p>
  <div v-if="error" role="alert" class="error">
    {{ error }} <button class="text-button" @click="load">Prøv igen</button>
  </div>
  <WorkspaceUsage v-if="isAdmin" />
  <div class="collection-toolbar">
    <h2>
      Datasæt <span class="count">{{ datasets.length }}</span>
    </h2>
    <label
      >Status<select v-model="status" aria-label="Status" @change="load">
        <option value="active">Aktive</option>
        <option value="archived">Arkiverede</option>
      </select></label
    >
    <button
      v-if="isAdmin"
      class="button secondary"
      @click="
        showDeletions = !showDeletions;
        showDeletions && loadDeletions();
      "
    >
      Sletteopgaver
    </button>
    <label class="search-field"
      ><SearchIcon :size="17" /><input
        v-model="search"
        placeholder="Find et datasæt"
        aria-label="Find et datasæt"
    /></label>
  </div>
  <section
    v-if="showDeletions"
    class="working-panel"
    aria-label="Sletteopgaver"
  >
    <div class="dialog-heading">
      <h2>Sletteopgaver</h2>
      <button class="text-button" @click="loadDeletions">Opdatér status</button>
    </div>
    <p v-if="!deletions.length" class="muted">Der er ingen sletteopgaver.</p>
    <div v-for="op in deletions" :key="op.id" class="deletion-operation">
      <strong>{{ deletionStatus[op.status] }}</strong
      ><span
        >Bestilt {{ dateTime(op.requested_at) }} · Planlagt
        {{ dateTime(op.purge_after) }}</span
      ><small>Datasæt {{ op.dataset_id }}</small>
      <p v-if="op.last_error" class="error">{{ op.last_error }}</p>
      <button
        v-if="op.status === 'failed'"
        class="button secondary"
        @click="retryDeletion(op.id)"
      >
        Genoptag oprydning
      </button>
    </div>
  </section>
  <div v-if="loading" class="empty-state" role="status">
    Henter dine datasæt…
  </div>
  <div
    v-else-if="!datasets.length && status === 'archived'"
    class="empty-state"
  >
    Du har ingen arkiverede datasæt.
  </div>
  <div v-else-if="!datasets.length" class="first-dataset">
    <div class="empty-icon"><DatabaseIcon :size="30" /></div>
    <h2>Giv dine data et hjem</h2>
    <p>
      Forbind en API, upload en fil eller start med en tom tabel. Du bestemmer
      felterne og godkender ændringerne.
    </p>
    <div class="start-options">
      <RouterLink to="/new?mode=http"
        ><LinkIcon :size="21" /><strong>Forbind en API</strong
        ><span>Hent data igen og igen.</span
        ><ArrowRightIcon :size="18" /></RouterLink
      ><RouterLink to="/new?mode=file"
        ><UploadIcon :size="21" /><strong>Upload en fil</strong
        ><span>CSV og Excel, med preview.</span
        ><ArrowRightIcon :size="18" /></RouterLink
      ><RouterLink to="/new?mode=manual"
        ><TableIcon :size="21" /><strong>Begynd med en tabel</strong
        ><span>Dine felter, din indtastning.</span><ArrowRightIcon :size="18"
      /></RouterLink>
    </div>
  </div>
  <div v-else class="dataset-list">
    <div class="dataset-list-head">
      <span>Navn</span><span>Rækker</span><span>Seneste synkronisering</span
      ><span />
    </div>
    <RouterLink
      v-for="d in filtered"
      :key="d.id"
      :to="'/datasets/' + d.id"
      class="dataset-list-row"
      ><div class="dataset-title">
        <span class="dataset-icon"
          ><DatasetAvatar :dataset="d" :size="24"
        /></span>
        <div>
          <strong>{{ d.name }}</strong
          ><span>{{ d.description || "Et samlet sted for dine data" }}</span>
        </div>
      </div>
      <span class="numeric dataset-count"
        >{{ Number(d.record_count).toLocaleString("da-DK")
        }}<small>rækker</small></span
      ><span class="sync-time">{{ dateTime(d.last_sync) }}</span
      ><ArrowRightIcon :size="18"
    /></RouterLink>
    <p v-if="!filtered.length" class="empty-state">
      Ingen datasæt matcher “{{ search }}”.
    </p>
  </div>
  <div class="home-note">
    <span class="note-rule" />
    <p>
      <strong>Et datasæt er kun begyndelsen.</strong> Lav et dashboard, spørg til
      dine data, og opret en regel, der følger med for dig.
    </p>
  </div>
</template>
